/**
 * BookingFormDrawer — formulario completo de creación de reservas desde el admin.
 *
 * Side-drawer modal (slide-in desde la derecha, mismo patrón que
 * `BookingDetailDrawer`). Permite a `superadmin` y `center_admin` crear
 * reservas manuales — útil para atender llamadas o reservar in-store.
 *
 * Endpoints usados:
 *   - `GET  /api/admin/centers` (vía adminCentersApi.list)
 *   - `GET  /api/admin/services` (vía useServices)
 *   - `GET  /api/admin/staff` (vía useStaff)
 *   - `GET  /api/public/bookings/availability` (vía adminAvailabilityApi)
 *   - `POST /api/admin/bookings` (vía adminBookingsExtrasApi.create)
 *
 * Estado mínimo viable: cuando cambian centro/servicio/staff/fecha,
 * los slots se refrescan automáticamente (useQuery). Errors específicos:
 *   - `SLOT_TAKEN` → mensaje + reload de slots.
 *   - ValidationError → muestra `error.details` (server) o mensaje genérico.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Icons from '@components/Icons';
import {
  adminAvailabilityApi,
  adminBookingsExtrasApi,
  type AdminCreateBookingBody,
} from '@api/client';
import { ApiRequestError } from '@api/client';
import { useServices, useStaff } from '@hooks/useAdminResources';
import type { Center, Service, Staff } from '@api/types';
import { CustomerAutocomplete, type CustomerSelection } from './CustomerAutocomplete';
import { todayInTimezone } from '@lib/timezone';

interface Props {
  /** Centro pre-seleccionado (sea el filtro actual o el único centro del usuario). */
  initialCenterId: string | null;
  /** Centros que el caller puede usar como destino. Vacío = no debería haberse abierto. */
  availableCenters: readonly Center[];
  /** True = `center` dropdown bloqueado. P.ej. `center_admin` con un solo centro. */
  centerLocked: boolean;
  onClose: () => void;
  /** Se invoca tras crear con éxito (para que el padre invalide y cierre). */
  onCreated: () => void;
}

function fmtHourMinute(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso.slice(11, 16);
  }
}

function todayIsoDate(): string {
  // M-9 closeout: antes `new Date().toISOString().slice(0,10)` usaba UTC
  // del navegador. Admin es ES-only por diseño, así que default a la TZ
  // española del producto para que el date picker abra hoy en Madrid
  // incluso si el laptop del operador está en otra zona.
  return todayInTimezone('Europe/Madrid');
}

export function BookingFormDrawer({
  initialCenterId,
  availableCenters,
  centerLocked,
  onClose,
  onCreated,
}: Props) {
  const qc = useQueryClient();

  // ── State ────────────────────────────────────────────────────────────────
  const [centerId, setCenterId] = useState<string>(
    initialCenterId && availableCenters.some((c) => c.id === initialCenterId)
      ? initialCenterId
      : (availableCenters[0]?.id ?? ''),
  );
  const [customer, setCustomer] = useState<CustomerSelection | null>(null);
  const [serviceId, setServiceId] = useState<string>('');
  // `null` = "Cualquier profesional" (pool / first-fit). `''` = nada elegido.
  const [staffId, setStaffId] = useState<string | null>('');
  const [date, setDate] = useState<string>(todayIsoDate());
  const [slotIso, setSlotIso] = useState<string>('');
  const [customerNote, setCustomerNote] = useState<string>('');
  const [notifyOff, setNotifyOff] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // ── Datasources ──────────────────────────────────────────────────────────
  const { data: services } = useServices();
  const { data: allStaff } = useStaff(centerId);

  // Staff filtrado al centro (multi-centro: `centerIds` array).
  const staffForCenter: Staff[] = useMemo(() => {
    if (!allStaff) return [];
    if (!centerId) return [];
    return allStaff.filter((s) => s.active && s.centerIds.includes(centerId));
  }, [allStaff, centerId]);

  // Slots: solo cargar cuando hay centro+servicio+fecha. `staffId === ''`
  // significa "aún no decidido"; lo mapeamos a null (pool) al server.
  const slotsEnabled = !!centerId && !!serviceId && !!date;
  const staffIdForQuery: string | null = staffId === '' || staffId === null ? null : staffId;

  const slotsQuery = useQuery({
    queryKey: ['admin-availability', centerId, serviceId, staffIdForQuery, date],
    enabled: slotsEnabled,
    staleTime: 0,
    queryFn: () => adminAvailabilityApi.list(centerId, serviceId, staffIdForQuery, date),
  });

  // Si el slot seleccionado deja de existir (cambio de filtros), reset.
  useEffect(() => {
    if (!slotsQuery.data) return;
    if (slotIso && !slotsQuery.data.some((s) => s.startIso === slotIso)) {
      setSlotIso('');
    }
  }, [slotsQuery.data, slotIso]);

  // ── Submit ───────────────────────────────────────────────────────────────
  const createMut = useMutation({
    mutationFn: (body: AdminCreateBookingBody) => adminBookingsExtrasApi.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bookings'] });
      onCreated();
    },
    onError: (err) => {
      handleCreateError(err);
    },
  });

  function handleCreateError(err: unknown) {
    if (err instanceof ApiRequestError) {
      if (err.code === 'SLOT_TAKEN') {
        setSubmitError('Ese slot ya está ocupado. Hemos refrescado la lista.');
        setSlotIso('');
        void slotsQuery.refetch();
        return;
      }
      // ValidationError: { error, code, details } — el backend usa `details`
      // como objeto field→message. Si está, lo mapeamos a fieldErrors.
      const details = err.details;
      if (details && typeof details === 'object') {
        const next: Record<string, string> = {};
        for (const [k, v] of Object.entries(details as Record<string, unknown>)) {
          if (typeof v === 'string') next[k] = v;
          else if (Array.isArray(v) && typeof v[0] === 'string') next[k] = v[0];
        }
        if (Object.keys(next).length > 0) {
          setFieldErrors(next);
          setSubmitError('Revisa los campos marcados.');
          return;
        }
      }
      setSubmitError(err.message || 'No pudimos crear la reserva.');
      return;
    }
    setSubmitError('No pudimos crear la reserva. Inténtalo de nuevo.');
  }

  function validateLocal(): string | null {
    if (!centerId) return 'Selecciona un centro.';
    if (!customer) return 'Selecciona o crea un cliente.';
    if (!serviceId) return 'Selecciona un servicio.';
    if (!date) return 'Elige una fecha.';
    if (!slotIso) return 'Elige un slot.';
    return null;
  }

  function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setSubmitError(null);
    setFieldErrors({});
    const localErr = validateLocal();
    if (localErr) { setSubmitError(localErr); return; }

    const body: AdminCreateBookingBody = {
      centerId,
      serviceId,
      staffId: staffIdForQuery,
      startTime: slotIso,
      ...(customer?.kind === 'existing'
        ? { customerId: customer.customerId }
        : {}),
      ...(customer?.kind === 'new'
        ? {
            customer: {
              name: customer.name,
              email: customer.email,
              ...(customer.phone ? { phone: customer.phone } : {}),
            },
          }
        : {}),
      ...(customerNote.trim() ? { customerNote: customerNote.trim() } : {}),
      notifyCustomer: !notifyOff,
    };

    createMut.mutate(body);
  }

  // ── Render ───────────────────────────────────────────────────────────────
  const submitting = createMut.isPending;
  const slots = slotsQuery.data ?? [];

  return (
    <div className="modal-scrim" data-variant="drawer" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => { e.stopPropagation(); }}
        style={{ width: 'min(560px, 100%)' }}
      >
        <div className="modal-head">
          <div className="modal-title">Nueva reserva</div>
          <button
            type="button"
            className="icon-btn"
            data-variant="ghost"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <Icons.X size={14} />
          </button>
        </div>

        <form className="modal-body" onSubmit={handleSubmit}>
          {/* Centro */}
          <div className="field">
            <label className="field-label">Centro</label>
            <select
              className="select"
              value={centerId}
              disabled={centerLocked}
              onChange={(e) => {
                setCenterId(e.target.value);
                // Cambiar centro invalida staff y slots seleccionados.
                setStaffId('');
                setSlotIso('');
              }}
            >
              {availableCenters.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {fieldErrors.centerId && (
              <p className="field-help" data-tone="danger">{fieldErrors.centerId}</p>
            )}
          </div>

          {/* Cliente */}
          <div className="field">
            <label className="field-label">Cliente</label>
            <CustomerAutocomplete
              centerId={centerId || undefined}
              value={customer}
              onChange={setCustomer}
            />
          </div>

          {/* Servicio */}
          <div className="field">
            <label className="field-label">Servicio</label>
            <select
              className="select"
              value={serviceId}
              onChange={(e) => {
                setServiceId(e.target.value);
                setSlotIso('');
              }}
            >
              <option value="">— Selecciona servicio —</option>
              {(services ?? [])
                .filter((s: Service) => s.active)
                .map((s: Service) => (
                  <option key={s.id} value={s.id}>
                    {s.name} · {s.durationMinutes} min
                  </option>
                ))}
            </select>
            {fieldErrors.serviceId && (
              <p className="field-help" data-tone="danger">{fieldErrors.serviceId}</p>
            )}
          </div>

          {/* Staff */}
          <div className="field">
            <label className="field-label">Profesional</label>
            <select
              className="select"
              value={staffId ?? '__pool__'}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') setStaffId('');
                else if (v === '__pool__') setStaffId(null);
                else setStaffId(v);
                setSlotIso('');
              }}
            >
              <option value="">— Selecciona profesional —</option>
              <option value="__pool__">Cualquier profesional</option>
              {staffForCenter.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            {fieldErrors.staffId && (
              <p className="field-help" data-tone="danger">{fieldErrors.staffId}</p>
            )}
          </div>

          {/* Fecha */}
          <div className="field">
            <label className="field-label">Fecha</label>
            <input
              type="date"
              className="input"
              value={date}
              min={todayIsoDate()}
              onChange={(e) => {
                setDate(e.target.value);
                setSlotIso('');
              }}
            />
          </div>

          {/* Slot */}
          <div className="field">
            <label className="field-label">Hora</label>
            <select
              className="select"
              value={slotIso}
              disabled={!slotsEnabled || slotsQuery.isLoading}
              onChange={(e) => { setSlotIso(e.target.value); }}
            >
              <option value="">
                {!slotsEnabled
                  ? '— Selecciona centro, servicio y fecha —'
                  : slotsQuery.isLoading
                    ? 'Cargando…'
                    : slots.length === 0
                      ? 'Sin slots libres'
                      : '— Selecciona hora —'}
              </option>
              {slots.map((s) => (
                <option key={s.startIso} value={s.startIso}>
                  {fmtHourMinute(s.startIso)} – {fmtHourMinute(s.endIso)}
                </option>
              ))}
            </select>
            {fieldErrors.startTime && (
              <p className="field-help" data-tone="danger">{fieldErrors.startTime}</p>
            )}
          </div>

          {/* Nota interna */}
          <div className="field">
            <label className="field-label">
              Nota interna <span className="soft">(opcional)</span>
            </label>
            <textarea
              className="input"
              rows={3}
              value={customerNote}
              onChange={(e) => { setCustomerNote(e.target.value); }}
              placeholder="Información que el cliente vería en su confirmación…"
            />
          </div>

          {/* Notificar al cliente */}
          <div className="switch-row">
            <div className="switch-row-info">
              <div className="switch-row-name">No notificar al cliente</div>
              <div className="switch-row-desc">
                Si está activo, no se envía email de confirmación al crear.
              </div>
            </div>
            <button
              type="button"
              className="switch"
              data-on={notifyOff ? 'true' : 'false'}
              onClick={() => { setNotifyOff((v) => !v); }}
              aria-label="No notificar al cliente"
            />
          </div>

          {submitError && (
            <div className="cust-ac-err" role="alert">
              {submitError}
            </div>
          )}
        </form>

        <div className="modal-foot">
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn"
            data-variant="primary"
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting ? 'Creando…' : 'Crear reserva'}
          </button>
        </div>
      </div>
    </div>
  );
}
